/**
 * A call on a rep's own number, in a room of its own (ADR-032): the rules the phone applies to
 * what the server says about such a room.
 *
 * Pure, and kept out of the components for the reason `lib/acd.ts` is: every rule here decides
 * what a rep is shown mid-call, and a rule that can only be exercised with two handsets and a
 * switch is a rule nobody re-checks. The server's answer (`RoomCall`, from
 * `GET /phone/room/calls/{call_id}`) is the event-tracker's picture of the room, a moment behind
 * the switch; the roster the phone already holds (`ConferenceState`) is the switch's own, live.
 * Where both say the same thing the roster wins, since it is faster.
 */

import { holdsInRoom, type AcdCall } from "@/lib/acd";
import type { RoomCall, RoomInvite, RoomMember } from "@/lib/types";

/** The prefix the platform mints direct rooms with (`services/shared/telephony/conference.py`). */
export const DIRECT_ROOM_PREFIX = "direct-";

/** The prefix of the room every call the ACD connects lives in (ADR-029), `call-<caller uuid>`. */
export const CALL_ROOM_PREFIX = "call-";

/** Whether a conference name is a direct room: a call on a rep's own number, not the rep's own
 *  `<ext>-room` and not one the ACD made. */
export function isDirectRoom(name: string | null | undefined): boolean {
  return typeof name === "string" && name.startsWith(DIRECT_ROOM_PREFIX);
}

/**
 * This phone's leg in a direct room, or null when it is in no such room.
 *
 * The roster names the leg that joined (`callId`); the room's name says what kind of room it
 * is. Both are needed: the rep's own `<ext>-room` and an ACD call's room reach the phone the
 * same way and must not get these controls.
 */
export function directRoomCallId(
  conference: { name: string; callId: string | null } | null,
): string | null {
  if (!conference || !isDirectRoom(conference.name)) return null;
  return conference.callId;
}

/** Whether a conference name is the room of a call the ACD connected (ADR-029). */
export function isCallRoom(name: string | null | undefined): boolean {
  return typeof name === "string" && name.startsWith(CALL_ROOM_PREFIX);
}

/**
 * This phone's leg in a room whose people it lists, or null: a direct call's room, or a campaign
 * call's (ADR-041). The rep's own `<ext>-room` has a roster of its own elsewhere.
 */
export function listedRoomCallId(
  conference: { name: string; callId: string | null } | null,
): string | null {
  if (!conference || !(isDirectRoom(conference.name) || isCallRoom(conference.name))) return null;
  return conference.callId;
}

/**
 * The conference the transfer rules are told about (`transferOptions`): none, on a campaign call.
 *
 * Those rules treat the phone's conference as a room the rep can move callers into, whose own leg
 * has no bridge partner to send anywhere. A campaign call's room is neither: nothing is moved
 * into it, and its caller is sent to a number by core-api moving the caller's own leg. Its roster
 * reaches the phone since ADR-041, and without this it would take that room for the rep's own.
 */
export function transferRoom<T extends { name: string }>(conference: T | null): T | null {
  return conference && isCallRoom(conference.name) ? null : conference;
}

/** Whether a member of the room is one of the firm's reps, by the server's reading of the leg. */
export function isRepMember(member: Pick<RoomMember, "kind">): boolean {
  return member.kind === "rep" || member.kind === "room";
}

export interface RoomParties {
  /** The outside parties: the caller, or whoever a rep dialled. Usually one. */
  callers: RoomMember[];
  /** The firm's own legs, this phone's included. */
  reps: RoomMember[];
  /** This phone's leg, once the switch has reported it in the room. */
  self: RoomMember | null;
}

export function roomParties(room: RoomCall | null | undefined): RoomParties {
  const members = room?.members ?? [];
  return {
    callers: members.filter((m) => !isRepMember(m)),
    reps: members.filter(isRepMember),
    self: members.find((m) => m.is_self) ?? null,
  };
}

/** The colleague's name for a row, or their extension, or a stand-in for a leg nothing names. */
export function colleagueLabel(
  extension: string | null | undefined,
  name: string | null | undefined,
): string {
  return name || (extension ? `Extension ${extension}` : "a colleague");
}

/**
 * A briefing in progress on a direct call, as seen from this phone, or null when there is none.
 *
 * Two sides of it. The rep who asked (`asker`) sees the colleague's phone ringing, then the two
 * of them talking with the caller on hold; from there they connect them, make it a three-way or
 * take the caller back. The colleague (`invitee`) sees that they are being briefed and that the
 * caller is waiting. A consult whose hold has been lifted is over — the three of them are on the
 * call — so `talking` requires the caller to be held: that is the one fact that tells a briefing
 * from a three-way, and the server reads it off the switch's own hold events.
 */
export type ConsultState =
  | {
      role: "asker";
      phase: "ringing";
      channelId: string;
      withExtension: string | null;
      withName: string;
    }
  | {
      role: "asker";
      phase: "talking";
      channelId: string;
      withExtension: string | null;
      withName: string;
    }
  | {
      role: "invitee";
      phase: "talking";
      byExtension: string | null;
      byName: string;
      /** Whether the rep who asked is still on the call. When they are not, the caller is on hold
       *  with nobody, and this phone takes them (`shouldTakeCaller`). */
      askerPresent: boolean;
    };

/** Whether an invite is still ringing: an ended one is kept a short while only to say why. */
export function isRinging(invite: Pick<RoomInvite, "ended_cause" | "ended_at">): boolean {
  return !invite.ended_cause && !invite.ended_at;
}

/** The invites still ringing for the room. */
export function ringingInvites(room: RoomCall | null | undefined): RoomInvite[] {
  return (room?.invites ?? []).filter(isRinging);
}

/** Whether an invite is for an outside number a rep is bringing in (ADR-065), not a colleague. */
export function isOutsideInvite(invite: Pick<RoomInvite, "extension" | "number">): boolean {
  return !invite.extension && Boolean(invite.number);
}

/** Whether a member is an outside participant a rep brought in (ADR-065): not one of the firm,
 *  and not the party the call was with either. */
export function isParticipant(member: Pick<RoomMember, "kind" | "invited_by">): boolean {
  return !isRepMember(member) && member.invited_by !== null;
}

export function consultState(
  room: RoomCall | null | undefined,
  myExtension: string | null | undefined,
): ConsultState | null {
  if (!room || !room.room || !room.joined || !myExtension) return null;
  const { self, reps } = roomParties(room);
  if (!self) return null;

  const ringing = ringingInvites(room).find(
    (i) => i.mode === "consult" && i.invited_by === myExtension && !isOutsideInvite(i),
  );
  if (ringing) {
    return {
      role: "asker",
      phase: "ringing",
      channelId: ringing.channel_id,
      withExtension: ringing.extension,
      withName: colleagueLabel(ringing.extension, ringing.name),
    };
  }
  if (!room.caller_held) return null;

  const briefed = reps.find(
    (m) => !m.is_self && m.invite_mode === "consult" && m.invited_by === myExtension,
  );
  if (briefed) {
    return {
      role: "asker",
      phase: "talking",
      channelId: briefed.channel_id,
      withExtension: briefed.extension,
      withName: colleagueLabel(briefed.extension, briefed.name),
    };
  }
  if (self.invite_mode === "consult" && self.invited_by) {
    const asker = reps.find((m) => m.extension === self.invited_by) ?? null;
    return {
      role: "invitee",
      phase: "talking",
      byExtension: self.invited_by,
      byName: colleagueLabel(self.invited_by, asker?.name),
      askerPresent: asker !== null,
    };
  }
  return null;
}

/**
 * Whether this phone should take the caller off hold on its own: it was brought in to be
 * briefed, the caller is still on hold, and the rep who was briefing it has gone. Left alone, the
 * caller would sit on music with nobody coming (the rule ADR-027 chose for the ACD, kept here).
 */
export function shouldTakeCaller(state: ConsultState | null): boolean {
  return state?.role === "invitee" && !state.askerPresent;
}

/** What a ringing phone is told about a call it is being brought into, or null for any other. */
export function inviteText(room: RoomCall | null | undefined): { title: string; detail: string } | null {
  if (!room || !room.room || room.joined || !room.invited_by) return null;
  const who = colleagueLabel(room.invited_by, room.invited_by_name);
  const { callers } = roomParties(room);
  const caller = callers[0];
  const withWhom = caller ? ` with ${caller.name ?? caller.number ?? "a caller"}` : "";
  return room.invite_mode === "consult"
    ? {
        title: `${who} wants a word before handing over a call${withWhom}`,
        detail: "The caller waits on hold while you talk.",
      }
    : {
        title: `${who} is adding you to a call${withWhom}`,
        detail: "You join the call as it is.",
      };
}

/** How many people are on the call, in words. */
export function peopleOnCall(count: number): string {
  return count === 1 ? "1 person" : `${count} people`;
}

/** The colleagues a rep can bring in or send the caller to, from the room's point of view:
 *  whoever is already on the call, or already ringing, is not offered again. */
export function alreadyOnCall(room: RoomCall | null | undefined, extension: string): boolean {
  if (!room) return false;
  return (
    room.members.some((m) => isRepMember(m) && m.extension === extension) ||
    ringingInvites(room).some((i: RoomInvite) => i.extension === extension)
  );
}

/**
 * An outside number being brought into the call (ADR-065), as seen from this phone, or null.
 *
 * Like a colleague's briefing (`consultState`) but with an outside party: their phone ringing,
 * then the two of them talking with the caller on hold, and for a short while afterwards the
 * reason a ring came to nothing. Once the caller is off hold with the newcomer still there the
 * briefing is over and the roster is the whole story. Every rep on the call sees it, not only
 * the one who asked: on a campaign call the setter and the closer are both moderators, and
 * either may end it (Mustafa, 2026-09-24: reps can remove the added person).
 */
export type ParticipantState =
  | { phase: "ringing"; channelId: string; name: string; number: string | null }
  | { phase: "talking"; channelId: string; name: string; number: string | null }
  | {
      phase: "ended";
      channelId: string;
      name: string;
      number: string | null;
      /** The switch's hangup cause. */
      cause: string;
    };

export function participantState(room: RoomCall | null | undefined): ParticipantState | null {
  if (!room || !room.room || !room.joined) return null;
  const invites = room.invites.filter(isOutsideInvite);
  const ringing = invites.find(isRinging);
  if (ringing) {
    return {
      phase: "ringing",
      channelId: ringing.channel_id,
      name: participantLabel(ringing.name, ringing.number),
      number: ringing.number ?? null,
    };
  }
  const briefed = room.caller_held ? room.members.find((m) => isParticipant(m) && !m.held) : null;
  if (briefed) {
    return {
      phase: "talking",
      channelId: briefed.channel_id,
      name: participantLabel(briefed.name, briefed.number),
      number: briefed.number,
    };
  }
  const ended = invites.find((i) => !isRinging(i));
  if (ended) {
    return {
      phase: "ended",
      channelId: ended.channel_id,
      name: participantLabel(ended.name, ended.number),
      number: ended.number ?? null,
      cause: ended.ended_cause ?? "UNKNOWN",
    };
  }
  return null;
}

/** The newcomer's name for a row, or their number, or a stand-in. */
export function participantLabel(
  name: string | null | undefined,
  number: string | null | undefined,
): string {
  return name || number || "the participant";
}

/**
 * Why a ring came to nothing, in the words the manual dial uses for the same causes
 * (`lib/manual-call.ts`), so a rep reads the same thing for the same event.
 */
export function participantEndedText(cause: string, name: string): { title: string; detail: string } {
  switch (cause.toUpperCase()) {
    case "ORIGINATOR_CANCEL":
      return { title: "Stopped", detail: `You stopped ringing ${name}.` };
    case "NO_ANSWER":
    case "NO_USER_RESPONSE":
    case "ALLOTTED_TIMEOUT":
      return { title: `${name} did not answer`, detail: "They didn't pick up. The call goes on." };
    case "USER_BUSY":
      return { title: `${name} is busy`, detail: "The line was busy. Try again in a minute." };
    case "UNALLOCATED_NUMBER":
    case "NO_ROUTE_DESTINATION":
    case "INVALID_NUMBER_FORMAT":
      return { title: "Number not in service", detail: "The carrier says this number does not exist." };
    case "CALL_REJECTED":
      return { title: `${name} rejected the call`, detail: "The far end refused it." };
    default:
      return { title: `Could not reach ${name}`, detail: `The call did not connect (${cause}).` };
  }
}


/**
 * Which of the three commands holds the caller on this call — the decision behind the Hold tile
 * and the Hold on a roster row, in one place because getting it wrong is silent.
 *
 *   "acd"     a call the ACD connected, in its room (ADR-041). `POST /acd/calls/{caller}/hold`.
 *             core-api REFUSES `/phone/room/hold` on one of the ACD's rooms with a 409
 *             (`room_calls.room_of` takes direct rooms only): the ACD holds the caller for a
 *             handoff, and a second path to the same switch commands would act behind its back.
 *   "direct"  a call on the rep's own number, in a room of its own (ADR-032).
 *             `POST /phone/room/hold`, which holds every member who is not a rep.
 *   "phone"   a plain bridged call: the phone's own hold, over verto. Wrong on either room —
 *             it holds the REP's leg and leaves the caller hearing nobody.
 *
 * Null for no call at all.
 */
export function holdPath(
  callId: string | null,
  acd: AcdCall | null,
  conference: { name: string; callId: string | null } | null,
): "acd" | "direct" | "phone" | null {
  if (callId === null) return null;
  if (directRoomCallId(conference) === callId) return "direct";
  if (holdsInRoom(acd, callId)) return "acd";
  return "phone";
}
